import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { auth, functions } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import { trustedStripeCheckoutUrl } from '../lib/redirects';
import { defaultAuthenticatedPath } from '../lib/tenantRouting';

type SelectedPlanId = 'core' | 'pro' | 'command';
type SignupStep = 0 | 1 | 2;

const businessTypeOptions = [
  'Gym',
  'Martial Arts',
  'Yoga Studio',
  'CrossFit',
  'Personal Trainer',
  'Other',
];

const planCopy: Record<SelectedPlanId, { name: string; price: string; bullets: string[] }> = {
  core: {
    name: 'Core',
    price: '$29/mo',
    bullets: ['Member management', 'Recurring & on-demand billing', 'Attendance tracking', 'Online appointments', 'Digital waivers', 'Free included gym website'],
  },
  pro: {
    name: 'Pro',
    price: '$49/mo',
    bullets: ['Everything in Core', 'Leads pipeline', 'Belt & rank progression tracking', 'Point of sale (POS)', 'Inventory management', 'Marketing automation'],
  },
  command: {
    name: 'Command',
    price: '$99/mo',
    bullets: ['Everything in Pro', 'Multi-location operations', 'Advanced reporting', 'Priority support'],
  },
};

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const signupBackgroundSx = {
  minHeight: '100vh',
  backgroundImage: 'url(/bg.jpg)',
  backgroundRepeat: 'repeat',
  backgroundColor: '#f5f7fb',
};

const signupSteps = [
  {
    label: 'Create account',
    eyebrow: 'Step 1',
    description: 'Set up your login with email and password or connect Google first.',
  },
  {
    label: 'Business details',
    eyebrow: 'Step 2',
    description: 'Tell us who owns the workspace and what kind of gym or studio you run.',
  },
  {
    label: 'Choose plan',
    eyebrow: 'Step 3',
    description: 'Pick the FitStac plan and continue into Stripe Checkout.',
  },
] as const;

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, activeTenant, roles, refreshTenants } = useTenant();
  const [currentStep, setCurrentStep] = useState<SignupStep>(user ? 1 : 0);
  const requestedPlanId = searchParams.get('plan');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [businessType, setBusinessType] = useState('Gym');
  const [selectedPlanId, setSelectedPlanId] = useState<SelectedPlanId>(
    requestedPlanId === 'pro' || requestedPlanId === 'command' ? requestedPlanId : 'core'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const existingWorkspacePath = useMemo(
    () => defaultAuthenticatedPath(activeTenant, roles),
    [activeTenant, roles]
  );
  const activeStepMeta = signupSteps[currentStep];

  const startTenantSignup = httpsCallable(functions, 'startTenantSignup');
  const retryCheckout = httpsCallable(functions, 'createCheckoutSessionForTenant');

  useEffect(() => {
    if (user) {
      setCurrentStep((step) => (step === 0 ? 1 : step));

      if (!email && user.email) {
        setEmail(user.email);
      }

      if ((!firstName || !lastName) && user.displayName) {
        const nameParts = user.displayName.split(' ').filter(Boolean);
        if (!firstName && nameParts[0]) {
          setFirstName(nameParts[0]);
        }
        if (!lastName && nameParts.length > 1) {
          setLastName(nameParts.slice(1).join(' '));
        }
      }
    }
  }, [email, firstName, lastName, user]);

  const submitTenantSignup = async (payload: {
    firstName: string;
    lastName: string;
    phone: string;
    tenantName: string;
    businessType: string;
    planId: SelectedPlanId;
  }) => {
    const result = await startTenantSignup(payload);
    const data = result.data as { checkoutUrl?: string; tenantId?: string; reused?: boolean };
    if (!data.checkoutUrl) {
      throw new Error('Stripe checkout URL was not returned');
    }

    await refreshTenants();
    window.location.assign(trustedStripeCheckoutUrl(data.checkoutUrl));
  };

  const clearFeedback = () => {
    setError(null);
    setMessage(null);
  };

  const validateAccountStep = () => {
    if (!user) {
      if (!email.trim()) {
        throw new Error('Enter an email address to create the owner account.');
      }
      if (password.length < 6) {
        throw new Error('Use a password with at least 6 characters.');
      }
    }
  };

  const validateDetailsStep = () => {
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !tenantName.trim() || !businessType.trim()) {
      throw new Error('Please complete the owner and business details before continuing.');
    }
  };

  const buildSignupPayload = () => {
    validateDetailsStep();

    return {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      tenantName: tenantName.trim(),
      businessType: businessType.trim(),
      planId: selectedPlanId,
    };
  };

  const handleCreateAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    clearFeedback();

    try {
      validateAccountStep();

      if (user) {
        setCurrentStep(1);
        setMessage('Your account is ready. Let’s set up the business details.');
      } else {
        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        if (credential.user.email) {
          setEmail(credential.user.email);
        }
        setCurrentStep(1);
        setMessage('Account created. Next up: owner and business details.');
      }
    } catch (signupError: any) {
      console.error('Account creation error:', signupError);
      setError(signupError.message || 'Unable to create the account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    clearFeedback();

    try {
      if (user) {
        setCurrentStep(1);
        setMessage('Your Google account is already connected.');
      } else {
        const credential = await signInWithPopup(auth, googleProvider);
        const nameParts = credential.user.displayName?.split(' ') || [];
        const nextFirstName = firstName || nameParts[0] || '';
        const nextLastName = lastName || nameParts.slice(1).join(' ') || '';
        setEmail(credential.user.email || '');
        setFirstName(nextFirstName);
        setLastName(nextLastName);
        setCurrentStep(1);
        setMessage('Google account connected. Next up: business details.');
      }
    } catch (signupError: any) {
      console.error('Google signup error:', signupError);
      setError(signupError.message || 'Unable to create the account with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToPlan = () => {
    clearFeedback();

    try {
      validateDetailsStep();
      setCurrentStep(2);
    } catch (detailsError: any) {
      setError(detailsError.message || 'Please complete the business details.');
    }
  };

  const handleBack = () => {
    clearFeedback();
    setCurrentStep((step) => Math.max(0, step - 1) as SignupStep);
  };

  const handleUseDifferentAccount = async () => {
    setLoading(true);
    clearFeedback();

    try {
      await signOut(auth);
      setCurrentStep(0);
      setPassword('');
      setMessage('Signed out. Create the owner account again to continue.');
    } catch (signOutError: any) {
      console.error('Sign out error:', signOutError);
      setError(signOutError.message || 'Unable to switch accounts right now.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCheckout = async () => {
    setLoading(true);
    clearFeedback();

    try {
      if (!auth.currentUser) {
        throw new Error('Create the owner account before starting checkout.');
      }

      const payload = buildSignupPayload();
      await updateProfile(auth.currentUser, {
        displayName: `${payload.firstName} ${payload.lastName}`.trim(),
      });
      await submitTenantSignup(payload);
    } catch (signupError: any) {
      console.error('Signup error:', signupError);
      setError(signupError.message || 'Unable to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeCheckout = async () => {
    if (!activeTenant) return;

    setLoading(true);
    clearFeedback();

    try {
      const result = await retryCheckout({
        tenantId: activeTenant.tenantId,
        planId: activeTenant.planId || selectedPlanId,
      });
      const data = result.data as { checkoutUrl?: string };
      if (!data.checkoutUrl) {
        throw new Error('Stripe checkout URL was not returned');
      }

      window.location.assign(trustedStripeCheckoutUrl(data.checkoutUrl));
    } catch (retryError: any) {
      console.error('Retry checkout error:', retryError);
      setError(retryError.message || 'Unable to reopen checkout');
    } finally {
      setLoading(false);
    }
  };

  if (user && activeTenant && activeTenant.status === 'pending_payment') {
    return (
      <Box sx={{ ...signupBackgroundSx, py: 6 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4 }}>
            <Stack spacing={2.5}>
              <Typography variant="h4" fontWeight={700}>
                Resume your checkout
              </Typography>
              <Typography color="text.secondary">
                Your workspace for <strong>{activeTenant.name}</strong> is already set up and waiting on payment.
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
              {message && <Alert severity="success">{message}</Alert>}
              <Button variant="contained" size="large" disabled={loading} onClick={handleResumeCheckout}>
                {loading ? 'Opening checkout...' : 'Continue to Stripe'}
              </Button>
              <Button variant="text" component={RouterLink} to="/checkout/cancelled" sx={{ textTransform: 'none' }}>
                Review billing status instead
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (user && activeTenant && activeTenant.status !== 'pending_payment') {
    return (
      <Box sx={{ ...signupBackgroundSx, py: 6 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4 }}>
            <Stack spacing={2.5}>
              <Typography variant="h4" fontWeight={700}>
                You already have a workspace
              </Typography>
              <Typography color="text.secondary">
                We found an active FitStac workspace for this account, so we’re keeping you in the app instead of creating a duplicate business.
              </Typography>
              <Alert severity="info">
                Workspace: <strong>{activeTenant.name}</strong>
              </Alert>
              <Button variant="contained" onClick={() => navigate(existingWorkspacePath)}>
                Open workspace
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ ...signupBackgroundSx, py: { xs: 3, md: 6 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Box
                component="img"
                src="/logo.png"
                alt="FitStac"
                sx={{ width: 170, maxWidth: '70%', height: 'auto', mb: 3 }}
              />
              <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5 }}>
                Launch your studio on FitStac
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Create the owner account first, fill in the business details second, then choose the plan and head into Stripe Checkout. We only unlock the tenant after Stripe confirms the subscription.
              </Typography>
              <Stack spacing={1.5}>
                {signupSteps.map((step, index) => {
                  const active = currentStep === index;
                  const completed = currentStep > index;
                  return (
                    <Box
                      key={step.label}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: active ? 'primary.main' : 'divider',
                        bgcolor: active ? 'rgba(25, 118, 210, 0.08)' : 'rgba(255, 255, 255, 0.82)',
                        borderRadius: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: completed || active ? 'primary.main' : 'grey.300',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="overline" color="text.secondary">
                            {step.eyebrow}
                          </Typography>
                          <Typography fontWeight={700}>{step.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {step.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
              <Divider sx={{ my: 3 }} />
              <Stack spacing={1}>
                <Typography variant="overline" color="text.secondary">
                  Selected plan
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {planCopy[selectedPlanId].name}
                </Typography>
                <Typography color="text.secondary">{planCopy[selectedPlanId].price}</Typography>
                <Stack spacing={0.75} sx={{ pt: 1 }}>
                  {planCopy[selectedPlanId].bullets.map((bullet) => (
                    <Stack key={bullet} direction="row" spacing={1} alignItems="center">
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {bullet}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 4 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {activeStepMeta.label}
                  </Typography>
                  <Typography color="text.secondary">
                    {activeStepMeta.description}
                  </Typography>
                </Box>

                <Stepper activeStep={currentStep} alternativeLabel sx={{ display: { xs: 'none', sm: 'flex' } }}>
                  {signupSteps.map((step) => (
                    <Step key={step.label}>
                      <StepLabel>{step.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {error && <Alert severity="error">{error}</Alert>}
                {message && <Alert severity="success">{message}</Alert>}

                {currentStep === 0 && (
                  <Box component="form" onSubmit={handleCreateAccount}>
                    <Stack spacing={2.5}>
                      {user ? (
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 2.5,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Stack spacing={1}>
                            <Typography variant="overline" color="text.secondary">
                              Account connected
                            </Typography>
                            <Typography variant="h6" fontWeight={700}>
                              {user.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              This owner account is ready. Continue into the business setup, or switch accounts first.
                            </Typography>
                          </Stack>
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Email"
                              type="email"
                              value={email}
                              onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                              required
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Password"
                              type="password"
                              value={password}
                              onChange={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
                              required
                              inputProps={{ minLength: 6 }}
                            />
                          </Grid>
                        </Grid>
                      )}

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ flex: 1 }}>
                          {loading ? 'Preparing account...' : user ? 'Continue' : 'Create account'}
                        </Button>
                        <Button
                          type="button"
                          variant="outlined"
                          size="large"
                          disabled={loading}
                          startIcon={<GoogleIcon />}
                          onClick={handleGoogleSignup}
                          sx={{ flex: 1 }}
                        >
                          {user ? 'Continue with Google' : 'Use Google'}
                        </Button>
                      </Stack>

                      {user && (
                        <Button
                          type="button"
                          variant="text"
                          disabled={loading}
                          onClick={handleUseDifferentAccount}
                          sx={{ alignSelf: 'flex-start', textTransform: 'none', p: 0 }}
                        >
                          Use a different account
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )}

                {currentStep === 1 && (
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Owner first name"
                          value={firstName}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Owner last name"
                          value={lastName}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => setLastName(event.target.value)}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={email}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone number"
                          value={phone}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => setPhone(event.target.value)}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Business name"
                          value={tenantName}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => setTenantName(event.target.value)}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="Gym or studio type"
                          value={businessType}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => setBusinessType(event.target.value)}
                          required
                        >
                          {businessTypeOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>

                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                      <Button type="button" variant="text" size="large" onClick={handleBack} disabled={loading}>
                        Back
                      </Button>
                      <Button type="button" variant="contained" size="large" onClick={handleContinueToPlan} disabled={loading}>
                        Continue to plan
                      </Button>
                    </Stack>
                  </Box>
                )}

                {currentStep === 2 && (
                  <Box>
                    <Stack spacing={2}>
                      {Object.entries(planCopy).map(([planId, plan]) => {
                        const active = selectedPlanId === planId;
                        return (
                          <Box
                            key={planId}
                            sx={{
                              p: 2.5,
                              border: '1px solid',
                              borderColor: active ? 'primary.main' : 'divider',
                              bgcolor: active ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                              borderRadius: 2,
                              cursor: 'pointer',
                            }}
                            onClick={() => setSelectedPlanId(planId as SelectedPlanId)}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography fontWeight={700}>{plan.name}</Typography>
                              <Typography fontWeight={700}>{plan.price}</Typography>
                            </Stack>
                            <Stack spacing={0.75}>
                              {plan.bullets.map((bullet) => (
                                <Stack key={bullet} direction="row" spacing={1} alignItems="center">
                                  <CheckCircleOutlineIcon color={active ? 'primary' : 'action'} fontSize="small" />
                                  <Typography variant="body2" color="text.secondary">
                                    {bullet}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>

                    <Box
                      sx={{
                        mt: 3,
                        p: 2.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Typography variant="overline" color="text.secondary">
                        Ready for checkout
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {tenantName || 'Your business'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {firstName} {lastName} • {businessType} • {planCopy[selectedPlanId].name}
                      </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                      <Button type="button" variant="text" size="large" onClick={handleBack} disabled={loading}>
                        Back
                      </Button>
                      <Button type="button" variant="contained" size="large" onClick={handleStartCheckout} disabled={loading}>
                        {loading ? 'Starting checkout...' : 'Continue to checkout'}
                      </Button>
                    </Stack>
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary">
                  Already have an account? <Button component={RouterLink} to="/" sx={{ p: 0, minWidth: 0, textTransform: 'none' }}>Sign in</Button>
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default SignupPage;
